import { useMutation, useQuery, useQueryClient } from 'react-query';
import { chamarOktonBank, enviarOktonBank } from '../oktonbank';

// Depósito: dinheiro que sai do Caixa e entra na Conta Bancária.
// moedabanco_dep é a Conta de destino; moeda_dep é a forma de pagamento
// depositada. Nasce Pendente e é compensado quando o banco confirma.
export interface Deposito {
  id_dep: string;
  data_dep: string;
  valor_dep: string;
  envelope_dep: string | null;
  status_dep: string;
  observacao_dep: string | null;
  moedabanco_dep: string;
  moeda_dep: string;
  caixa_dep: string | null;
  planocontas_dep: string | null;
  id_cts: string | null;
  descricao_cts: string | null;
  // nome_moe é a forma de pagamento depositada (moeda_dep); a Moeda do
  // banco, que diz em que Conta o dinheiro caiu, vem separada.
  nome_moe: string | null;
  nomemoedabanco_dep: string | null;
  nome_pct: string | null;
  // A data da compensação mora no item do Depósito (depositoi), como no
  // IFLEX; a capa só guarda o status. Nula enquanto Pendente.
  datacompensacao_dep: string | null;
  // Depósito não tem coluna de conciliado: quem registra é a
  // conciliacaobancaria, e o back devolve a contagem de vínculos.
  conciliado_dep: string;
}

// Um cheque em maos, esperando deposito. No IFLEX ele e uma linha do
// financeiro com o banco preenchido; confban_fin guarda o codigo de
// compensacao, e nome_ban e o nome que veio do cadastro de Bancos.
export interface ChequeDisponivel {
  id_fin: string;
  documento_fin: string | null;
  nomecliente_fin: string | null;
  cpfcli_fin: string | null;
  confban_fin: string | null;
  nome_ban: string | null;
  nome_cli: string | null;
  valor_fin: string;
  vencimento_fin: string | null;
  status_fin: string;
}

/**
 * Cheques que ainda podem ser depositados.
 *
 * Mesma regra da tela de Depósito do IFLEX: o papel em mãos (Aberto) e o
 * que voltou do banco (Devolvido). O que já foi depositado ou compensado
 * não reaparece.
 */
export function useChequesDisponiveis(habilitado = true) {
  return useQuery(
    ['oktonbank-cheques-disponiveis'],
    async () => {
      const dados = await chamarOktonBank<{ rows: ChequeDisponivel[] }>(
        'OBFinanceiro',
        'listarChequesDisponiveis',
      );
      return Array.isArray(dados?.rows) ? dados.rows : [];
    },
    { enabled: habilitado },
  );
}

export interface NovoDeposito {
  conta?: string;
  moeda: string;
  data?: string;
  valor?: string;
  envelope?: string;
  observacao?: string;
  planocontas?: string;
  // Em branco o IFLEX resolve pelo Caixa aberto, como sempre fez.
  caixa?: string;
  // Códigos dos cheques escolhidos, em JSON, quando a Moeda depositada é de
  // Cheque de Terceiro. O valor do Depósito passa a ser a soma deles.
  cheques?: string;
  // Partindo da Conciliação: o lançamento dá Conta, valor e data, e o
  // Depósito já nasce conciliado.
  extrato?: string;
}

// status_dep do IFLEX.
export const STATUS_DEPOSITO: Record<string, string> = {
  '1': 'Pendente',
  '2': 'Compensado',
  '3': 'Devolvido',
  '4': 'Compensado parcial',
};

export const STATUSDEP_PENDENTE = '1';
export const STATUSDEP_COMPENSADO = '2';
export const STATUSDEP_COMPENSADOPARCIAL = '4';

// Só Depósito pendente é compensado, como o Monitor de Depósitos do IFLEX,
// que lista apenas os pendentes para a ação.
export function podeCompensar(d: Deposito): boolean {
  return d.status_dep === STATUSDEP_PENDENTE;
}

// E só o que foi compensado pode ser desfeito. O parcial entra aqui, mas o
// servidor ainda recusa quando há item devolvido: a devolução não foi feita
// por esta tela e desfazê-la junto apagaria o que só o IFLEX remonta.
export function podeDescompensar(d: Deposito): boolean {
  return (
    d.status_dep === STATUSDEP_COMPENSADO ||
    d.status_dep === STATUSDEP_COMPENSADOPARCIAL
  );
}

interface IResponseData {
  rows: Deposito[];
}

export function useDepositos(
  idConta?: string,
  dataIni = '',
  dataFim = '',
  status: 'all' | 'pending' | 'reconciled' = 'all',
  habilitado = true,
) {
  return useQuery(
    ['depositos', idConta ?? '', dataIni, dataFim, status],
    async () => {
      const dados = await chamarOktonBank<IResponseData>(
        'OBDeposito',
        'listar',
        {
          conta: idConta || '',
          dataini: dataIni,
          datafim: dataFim,
          status,
        },
      );
      return Array.isArray(dados?.rows) ? dados.rows : [];
    },
    { enabled: habilitado },
  );
}

function invalidar(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries('depositos');
  // Depositar leva os cheques escolhidos para Depositado: eles saem da
  // lista de disponíveis.
  queryClient.invalidateQueries('oktonbank-cheques-disponiveis');
  queryClient.invalidateQueries('lancamentos-financeiros');
  queryClient.invalidateQueries('monitor-bancario-resumo');
  queryClient.invalidateQueries('conciliacoes');
  queryClient.invalidateQueries('dashboard');
}

export function useCreateDeposito() {
  const queryClient = useQueryClient();

  return useMutation(
    (payload: NovoDeposito) =>
      enviarOktonBank<{ id_dep: number }>('OBDeposito', 'cadastrar', {
        ...payload,
      }),
    { onSuccess: () => invalidar(queryClient) },
  );
}

export interface DadosCompensacao {
  deposito: string;
  data: string;
  // Em branco o IFLEX grava meia-noite.
  hora: string;
}

export function useCompensarDeposito() {
  const queryClient = useQueryClient();

  return useMutation(
    (payload: DadosCompensacao) =>
      enviarOktonBank('OBDeposito', 'compensar', { ...payload }),
    { onSuccess: () => invalidar(queryClient) },
  );
}

export function useDescompensarDeposito() {
  const queryClient = useQueryClient();

  return useMutation(
    (id: string) =>
      enviarOktonBank('OBDeposito', 'descompensar', { deposito: id }),
    { onSuccess: () => invalidar(queryClient) },
  );
}

export function useDeleteDeposito() {
  const queryClient = useQueryClient();

  return useMutation(
    (id: string) => enviarOktonBank('OBDeposito', 'excluir', { deposito: id }),
    { onSuccess: () => invalidar(queryClient) },
  );
}
